import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const LINES = [
  { text: "Branding to", italic: false },
  { text: "Performance,", italic: false },
  { text: "Powered by", italic: false },
  { text: "Stellar.", italic: true },
];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.3 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src="https://images.pexels.com/photos/31650443/pexels-photo-31650443.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          className="h-[115%] w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#09090A]/70" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 45% at 72% 28%, rgba(139,92,246,0.16), transparent 70%)",
          }}
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-10 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="inline-block w-2 h-2 bg-[#8B5CF6] animate-pulse" aria-hidden="true" />
          <p className="font-accent text-xs tracking-[0.35em] uppercase text-[#A0A0AB]" data-testid="hero-overline">
            Programmatic Branding & Performance Marketing — India · Worldwide
          </p>
        </motion.div>

        <h1 className="font-display font-medium leading-[0.95] tracking-tight text-[clamp(3.2rem,10.5vw,10rem)]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`kinetic-line block ${line.italic ? "italic text-[#8B5CF6]" : ""}`}
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="max-w-md text-base md:text-lg text-[#A0A0AB] font-light leading-relaxed"
            data-testid="hero-subheadline"
          >
            From CTV & OTT branding at the best CPMs to performance campaigns
            engineered for CTR and ROI — we place your brand on brand-safe,
            top-tier worldwide inventory, integrated with leading DSP & SSP
            partners across India and the globe.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="flex items-center gap-6"
          >
            <a
              href="mailto:biz@adstellarmedia.com?subject=Let's%20Talk%20Strategy"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-[#8B5CF6] text-white px-8 py-5 font-accent text-xs tracking-[0.25em] uppercase hover:bg-[#A78BFA] transition-colors duration-300"
            >
              Let's Talk Strategy
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href="#manifesto"
              data-testid="hero-scroll-cue"
              aria-label="Scroll to manifesto"
              className="hidden md:inline-flex items-center justify-center w-14 h-14 border border-white/20 hover:border-[#8B5CF6] transition-colors duration-300"
            >
              <ArrowDown className="w-5 h-5 animate-bounce" />
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.5 }}
        className="relative z-10 border-t border-white/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between font-accent text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#52525B]">
          <span>Brand-Safe Inventory</span>
          <span className="hidden md:inline">Top DSP & SSP Partners — India & Worldwide</span>
          <span>500+ Global Clients</span>
        </div>
      </motion.div>
    </section>
  );
};
